import {escapeFilterValue, escapeString} from '@github-ui/filter-core'

import {MAX_QUERY_LENGTH, tokenizeQuery} from './tokenize-query'

export type TokenType =
  | 'label'
  | 'project'
  | 'author'
  | 'assignee'
  | 'milestone'
  | 'review'
  | 'reviewed-by'
  | 'user-review-requested'
  | 'review-requested'
  | 'type'

export type FilterQualifier = {
  key: string
  aliases?: readonly string[]
}

type FilterValuesOptions = {
  isNegated?: boolean
}

const tokensByType = (type: TokenType) =>
  new RegExp(`\\b${type}:("([^"]*)"|[^,\\s\\)]+)(\\s*,\\s*("(?:\\"|.)*?"|[^,\\s]+))*`, 'gim')

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const getTokensByType = (searchQuery: string, type: TokenType) => {
  const matches = searchQuery.match(tokensByType(type)) ?? []
  return matches.flatMap(match =>
    match
      .slice(`${type}:`.length)
      .split(',')
      .map(token => token.trim()),
  )
}

export function cleanConsecutiveSpaces(query: string): string {
  let inQuotes = false
  let newQuery = ''
  let previousCharacter = ''

  for (const character of query) {
    if (character === '"' && previousCharacter !== '\\') inQuotes = !inQuotes
    if (character !== ' ' || inQuotes || previousCharacter !== ' ') newQuery += character
    previousCharacter = character
  }

  return newQuery.trim()
}

function removeNoValueFilter(searchQuery: string, type: TokenType) {
  return getFilterValues(searchQuery, {key: 'no'})
    .filter(value => value.toLowerCase() === type)
    .reduce((query, value) => removeFilterValue(query, value, {key: 'no'}), searchQuery)
}

export const replaceTokensDifferentially = (
  searchQuery: string,
  newTokens: string[],
  type: TokenType,
  {treatNoValueAsLiteral = false} = {},
) => {
  const existingTokens = getTokensByType(searchQuery, type)
  const queryWithoutNoFilter = removeNoValueFilter(searchQuery, type)
  const tokensToAdd = newTokens.filter(token => !existingTokens.includes(token))
  const tokensToRemove = existingTokens.filter(token => !newTokens.includes(token))

  let newQuery = queryWithoutNoFilter
  for (const token of tokensToRemove) {
    newQuery = newQuery.replace(new RegExp(`-?${type}:${escapeRegExp(token)}`, 'g'), '')
  }
  for (const token of tokensToAdd) {
    newQuery =
      !treatNoValueAsLiteral && token === `no:${type}`
        ? `${newQuery} ${token} `
        : `${newQuery} ${type}:${escapeString(token)}`
  }

  return cleanConsecutiveSpaces(newQuery)
}

export const replaceAllFiltersByTypeInSearchQuery = (
  searchQuery: string,
  tokens: string[],
  type: TokenType,
  isNegatedFilter?: boolean,
) => {
  const cleanSearchQuery = removeNoValueFilter(searchQuery.replace(tokensByType(type), ''), type).trim()
  if (!tokens.length) return cleanConsecutiveSpaces(cleanSearchQuery)

  const formattedTokens = tokens.map(token => (token.includes(' ') ? `"${token}"` : token))
  const newQuery = isNegatedFilter ? `no:${type}` : `${type}:${formattedTokens.join(',')}`

  return cleanConsecutiveSpaces(`${cleanSearchQuery} ${newQuery}`)
}

/**
 * Reads values for a qualifier and its aliases. Included and excluded qualifiers are handled independently.
 */
export function getFilterValues(
  searchQuery: string,
  qualifier: FilterQualifier,
  {isNegated = false}: FilterValuesOptions = {},
) {
  const keys = qualifierKeys(qualifier)

  return tokenizeQuery(searchQuery).flatMap(segment => {
    if (segment.type !== 'filter' || segment.isNegated !== isNegated || !keys.has(segment.key.toLowerCase())) return []

    return segment.values
  })
}

/**
 * Appends one value unless the same qualifier, polarity, and value already exist.
 */
export function addFilterValue(
  searchQuery: string,
  value: string,
  qualifier: FilterQualifier,
  {isNegated = false}: FilterValuesOptions = {},
): string {
  if (searchQuery.length > MAX_QUERY_LENGTH) return searchQuery
  if (getFilterValues(searchQuery, qualifier, {isNegated}).includes(value)) return searchQuery

  const prefix = isNegated ? '-' : ''
  const updatedQuery = [searchQuery, `${prefix}${qualifier.key}:${escapeFilterValue(value)}`].filter(Boolean).join(' ')
  return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
}

/**
 * Removes one value while retaining the position of all other query segments.
 */
export function removeFilterValue(
  searchQuery: string,
  value: string,
  qualifier: FilterQualifier,
  {isNegated = false}: FilterValuesOptions = {},
) {
  if (searchQuery.length > MAX_QUERY_LENGTH) return searchQuery

  const keys = qualifierKeys(qualifier)
  let removed = false
  const updatedQuery = tokenizeQuery(searchQuery)
    .flatMap(segment => {
      if (
        segment.type !== 'filter' ||
        segment.isNegated !== isNegated ||
        !keys.has(segment.key.toLowerCase()) ||
        !segment.values.includes(value)
      ) {
        return segment.type === 'filter' ? segment.raw : segment.value
      }

      removed = true
      const remainingValues = segment.quotedValues.filter((_, index) => segment.values[index] !== value)
      return remainingValues.length > 0
        ? `${segment.isNegated ? '-' : ''}${segment.key}:${remainingValues.join(',')}`
        : []
    })
    .join(' ')

  return removed ? updatedQuery : searchQuery
}

function qualifierKeys({key, aliases = []}: FilterQualifier) {
  return new Set([key, ...aliases].map(value => value.toLowerCase()))
}
